import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import SelectionCheckBadge from '../SelectionCheckBadge'

const STEPS = [
  { id: 1, labelKey: 'listing.builder.stepPhone' },
  { id: 2, labelKey: 'listing.builder.stepCondition' },
  { id: 3, labelKey: 'listing.builder.stepUsage' },
  { id: 4, labelKey: 'listing.builder.stepKit' },
  { id: 5, labelKey: 'listing.builder.stepPhotos' },
  { id: 6, labelKey: 'listing.builder.stepPrice' },
  { id: 7, labelKey: 'listing.builder.stepContact' },
] as const

const STEP_COUNT = STEPS.length

type Props = {
  current: number
  maxReachable: number
  onGoTo: (step: number) => void
  /** Desktop wide layout: classic 7-step rail; mobile/tablet: compact chip. */
  variant?: 'classic' | 'compact'
}

export default function ListingBuilderStepper({
  current,
  maxReachable,
  onGoTo,
  variant = 'compact',
}: Props) {
  const { t } = useTranslation()
  const activeItemRef = useRef<HTMLLIElement>(null)
  const active = STEPS.find((step) => step.id === current) ?? STEPS[0]

  useEffect(() => {
    if (variant !== 'classic') return
    activeItemRef.current?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
  }, [current, variant])

  if (variant === 'compact') {
    return (
      <nav
        className="listing-builder-stepper listing-builder-stepper--compact builder-panel__stepper"
        aria-label={t('listing.builder.stepperAria')}
      >
        <p
          className="listing-builder-stepper__status"
          aria-current="step"
          aria-label={`${current}/${STEP_COUNT} ${t(active.labelKey)}`}
        >
          <span className="listing-builder-stepper__chip" aria-hidden="true">
            {current} / {STEP_COUNT}
          </span>
          <span className="listing-builder-stepper__title">{t(active.labelKey)}</span>
        </p>
      </nav>
    )
  }

  return (
    <nav
      className="listing-builder-stepper listing-builder-stepper--classic builder-panel__stepper"
      aria-label={t('listing.builder.stepperAria')}
    >
      <ol className="listing-builder-stepper__list">
        {STEPS.map((step) => {
          const done = step.id < current
          const activeStep = step.id === current
          const reachable = step.id <= maxReachable
          return (
            <li
              key={step.id}
              ref={activeStep ? activeItemRef : undefined}
              className={`listing-builder-stepper__item${
                activeStep ? ' listing-builder-stepper__item--active' : ''
              }${done ? ' listing-builder-stepper__item--done' : ''}`}
            >
              <button
                type="button"
                className="listing-builder-stepper__btn"
                disabled={!reachable && !activeStep}
                aria-current={activeStep ? 'step' : undefined}
                onClick={() => {
                  if (reachable || done) onGoTo(step.id)
                }}
              >
                <span className="listing-builder-stepper__dot">
                  <span
                    className={`listing-builder-stepper__dot-face listing-builder-stepper__dot-face--num${
                      done ? ' listing-builder-stepper__dot-face--hidden' : ''
                    }`}
                  >
                    {step.id}
                  </span>
                  <span
                    className={`listing-builder-stepper__dot-face listing-builder-stepper__dot-face--check${
                      done ? ' listing-builder-stepper__dot-face--visible' : ''
                    }`}
                    aria-hidden={!done}
                  >
                    <SelectionCheckBadge
                      accent="#2c9db4"
                      className="listing-builder-stepper__check-badge"
                    />
                  </span>
                </span>
                <span className="listing-builder-stepper__label">{t(step.labelKey)}</span>
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
